import React, { useCallback } from 'react';
import PopoverContainer from '../../_partials/PopoverContainer';
import PopperPositionedPopover from '../../PopperPositionedPopover';
import type {
  PopperPositioningProps,
  PrimaryPlacement,
  ReferenceChildrenProps,
} from '../../types';

export interface Props extends PopperPositioningProps {
  popoverContent:
    | React.ReactNode
    | ((props: {
        placement: PrimaryPlacement;
        updatePositioning?: () => void;
      }) => React.ReactNode);
  usePortal?: boolean;
  width?: number;
  height?: number;
  className?: string;
  wrapperClassName?: string;
  contentClassName?: string;
  directionalClassNames?: {
    [k in PrimaryPlacement]?: string;
  };
  role?: string;
  'aria-label'?: string;
  children?: React.ReactNode;
  innerRef?:
    | ((node: HTMLElement | null) => void)
    | React.MutableRefObject<HTMLElement | null>;

  onReferenceClick?: (e: React.MouseEvent) => void;
  onClickOutside?: (event: MouseEvent) => void;
  onMouseEnter?: (event: React.MouseEvent<HTMLElement>) => void;
  onMouseLeave?: (event: React.MouseEvent<HTMLElement>) => void;
  onKeyDown?: (event: React.KeyboardEvent<HTMLDivElement>) => void;
  /**
   * ONLY USE THIS IF YOU KNOW WHAT YOU ARE DOING
   * By default, children passed to this component will be the reference element for the popper,
   * or alternatively you can pass a renderReference prop. This is helpful if you need to make the
   * reference element be some inner node on the entire structure of the tooltip.
   */
  renderReference?: (props: ReferenceChildrenProps) => React.ReactNode;
}

const PositionOnlyPopperPopover = React.forwardRef<HTMLDivElement, Props>(
  (
    {
      isVisible = true,
      usePortal = true,
      placement,
      secondaryPlacement,
      referenceElement,
      width,
      height,
      offsetModifier,
      allowFlip,
      eventsEnabled,
      positionFixed,
      children,
      role,
      'aria-label': ariaLabel,
      className,
      wrapperClassName,
      contentClassName,
      directionalClassNames,
      popoverContent,
      innerRef,
      onClickOutside,
      onMouseEnter,
      onMouseLeave,
      onKeyDown,
      onReferenceClick,
      renderReference,
    },
    ref
  ) => {
    const renderPopover = useCallback(
      ({
        effectivePlacement,
        rootRef,
        contentStyle,
        contentAttributes,
        updatePositioning,
      }) => (
        <PopoverContainer
          ref={rootRef}
          innerRef={innerRef}
          placement={effectivePlacement}
          style={contentStyle}
          attributes={contentAttributes}
          wrapperClassName={wrapperClassName}
          contentClassName={contentClassName}
          directionalClassNames={directionalClassNames}
          width={width}
          height={height}
          updatePositioning={updatePositioning}
          onClickOutside={onClickOutside}
          role={role}
          aria-label={ariaLabel}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
          onKeyDown={onKeyDown}
        >
          {typeof popoverContent === 'function'
            ? popoverContent({
                placement: effectivePlacement,
                updatePositioning,
              })
            : popoverContent}
        </PopoverContainer>
      ),
      [
        innerRef,
        wrapperClassName,
        contentClassName,
        directionalClassNames,
        role,
        ariaLabel,
        onMouseEnter,
        onMouseLeave,
        onKeyDown,
        onClickOutside,
        width,
        height,
        popoverContent,
      ]
    );

    return (
      <PopperPositionedPopover
        ref={ref}
        className={className}
        isVisible={isVisible}
        usePortal={usePortal}
        offsetModifier={offsetModifier}
        allowFlip={allowFlip}
        eventsEnabled={eventsEnabled}
        positionFixed={positionFixed}
        placement={placement}
        secondaryPlacement={secondaryPlacement}
        referenceElement={referenceElement}
        onReferenceClick={onReferenceClick}
        renderReference={renderReference}
        renderPopover={renderPopover}
      >
        {children}
      </PopperPositionedPopover>
    );
  }
);
PositionOnlyPopperPopover.displayName = 'PositionOnlyPopperPopover';

export default PositionOnlyPopperPopover;
